import { useEffect, useMemo, useState } from 'react';
import { Check, HelpCircle, Info } from 'lucide-react';
import { admin, palette, radii, shadow, typography } from '../../../../styles/tokens';
import { normalizeEmail, normalizeName, normalizePhone } from '../shared/normalize';
import type { NormalizedRow } from '../shared/types';
import type {
  CompetitorImportProfile,
  CrewBitImportField,
} from '../competitor/competitorImportProfiles';
import type { ColumnMapping, CrewBitField, ParsedSheet } from './types';

interface MappingStepProps {
  parsed: ParsedSheet;
  onContinue: (rows: NormalizedRow[]) => void;
  // Optional competitor pre-fill. When set AND the uploaded file matches
  // the profile's fingerprint (≥3 headers), columns are pre-mapped via
  // profile.columnMap. When set but the fingerprint does NOT match, we
  // fall back to auto-detection and show a muted fallback notice.
  competitorProfile?: CompetitorImportProfile | null;
  competitorMatched?: boolean;
}

const FIELD_LABELS: Record<CrewBitField, string> = {
  ignore: "Don't import",
  name: 'Full name',
  first_name: 'First name',
  last_name: 'Last name',
  phone: 'Phone',
  email: 'Email',
};

function detectField(header: string, sampleCells: string[]): {
  field: CrewBitField;
  confidence: 'high' | 'low' | 'none';
} {
  const h = header.trim().toLowerCase();

  if (h) {
    if (h === 'name' || h === 'full name' || h === 'client name' || h === 'customer name') {
      return { field: 'name', confidence: 'high' };
    }
    if (h === 'first' || h === 'first name' || h === 'firstname' || h === 'given name') {
      return { field: 'first_name', confidence: 'high' };
    }
    if (h === 'last' || h === 'last name' || h === 'lastname' || h === 'surname' || h === 'family name') {
      return { field: 'last_name', confidence: 'high' };
    }
    if (h.includes('phone') || h.includes('mobile') || h === 'cell' || h === 'tel' || h === 'telephone') {
      return { field: 'phone', confidence: 'high' };
    }
    if (h.includes('email') || h === 'e-mail') {
      return { field: 'email', confidence: 'high' };
    }
  }

  // Fall back to sample content sniffing.
  const nonEmpty = sampleCells.filter(s => s && s.trim() !== '').slice(0, 10);
  if (nonEmpty.length === 0) return { field: 'ignore', confidence: 'none' };

  const emailCount = nonEmpty.filter(s => /@/.test(s) && /\./.test(s)).length;
  if (emailCount / nonEmpty.length >= 0.6) {
    return { field: 'email', confidence: 'high' };
  }

  const phoneCount = nonEmpty.filter(s => {
    const d = normalizePhone(s);
    return d.length >= 10 && d.length <= 15;
  }).length;
  if (phoneCount / nonEmpty.length >= 0.6) {
    return { field: 'phone', confidence: 'high' };
  }

  // A word-y column with spaces is probably a name; lean low-confidence.
  const wordyCount = nonEmpty.filter(s => /^[A-Za-z][A-Za-z .'\-,]*$/.test(s.trim())).length;
  if (wordyCount / nonEmpty.length >= 0.7) {
    return { field: 'name', confidence: 'low' };
  }

  return { field: 'ignore', confidence: 'none' };
}

function splitCommaName(raw: string): { first: string; last: string } | null {
  const idx = raw.indexOf(',');
  if (idx < 0) return null;
  const last = raw.slice(0, idx).trim();
  const first = raw.slice(idx + 1).trim();
  if (!first || !last) return null;
  return { first, last };
}

function combineName(first: string, last: string): string {
  const f = normalizeName(first);
  const l = normalizeName(last);
  return `${f} ${l}`.trim();
}

export default function MappingStep({
  parsed,
  onContinue,
  competitorProfile = null,
  competitorMatched = false,
}: MappingStepProps) {
  const { rows } = parsed;
  const columnCount = Math.max(0, ...rows.map(r => r.length));

  // Competitor pre-fill is only applied when the profile AND its fingerprint
  // both matched the uploaded file. If profile is set but didn't match, we
  // surface a one-line fallback notice and use generic auto-detection.
  const usingCompetitorProfile = !!competitorProfile && competitorMatched;

  // User override for the heuristic header detection. Unchecked → trust the
  // parser's detection; checked → force "first row is data." Toggling blows
  // away any manual column-mapping edits (user is correcting the structural
  // read; mapping overrides can't survive the shift).
  const [noHeaderOverride, setNoHeaderOverride] = useState(false);
  const effectiveHasHeader = noHeaderOverride ? false : parsed.hasHeader;

  const header = useMemo(
    () => (effectiveHasHeader && rows.length > 0 ? rows[0] : []),
    [effectiveHasHeader, rows],
  );
  const dataRows = useMemo(
    () => (effectiveHasHeader ? rows.slice(1) : rows),
    [effectiveHasHeader, rows],
  );

  const freshMappings = useMemo<ColumnMapping[]>(() => {
    const sampleSize = Math.min(dataRows.length, 20);

    // Pre-fill lookup: if we're running a competitor profile, match header
    // labels case-insensitively against the profile's columnMap. Hits get
    // 'high' confidence and bypass detectField entirely.
    const profileLookup = usingCompetitorProfile && competitorProfile
      ? new Map<string, CrewBitImportField>(
          Object.entries(competitorProfile.columnMap).map(([k, v]) => [
            k.trim().toLowerCase(),
            v,
          ]),
        )
      : null;

    return Array.from({ length: columnCount }, (_, col) => {
      const headerLabel = header[col] ?? '';

      if (profileLookup) {
        const hit = profileLookup.get(headerLabel.trim().toLowerCase());
        if (hit) {
          return {
            columnIndex: col,
            headerLabel,
            field: hit as CrewBitField,
            confidence: 'high' as const,
          };
        }
      }

      const samples = dataRows
        .slice(0, sampleSize)
        .map(r => r[col] ?? '')
        .filter(Boolean);
      const detected = detectField(headerLabel, samples);
      return {
        columnIndex: col,
        headerLabel,
        field: detected.field,
        confidence: detected.confidence,
      };
    });
  }, [columnCount, header, dataRows, usingCompetitorProfile, competitorProfile]);

  const [mappings, setMappings] = useState<ColumnMapping[]>(freshMappings);

  // Re-run detection when the user toggles "no header row." We deliberately
  // replace mappings wholesale — user edits made under the old detection no
  // longer correspond to the same sample window.
  useEffect(() => {
    setMappings(freshMappings);
  }, [freshMappings]);

  const nameMappedDirectly = mappings.some(m => m.field === 'name');
  const firstLastPair =
    mappings.some(m => m.field === 'first_name') &&
    mappings.some(m => m.field === 'last_name');
  const hasName = nameMappedDirectly || firstLastPair;

  const updateField = (col: number, field: CrewBitField) => {
    setMappings(prev =>
      prev.map(m =>
        m.columnIndex === col
          ? { ...m, field, confidence: 'high' as const }
          : m,
      ),
    );
  };

  const previewRows = dataRows.slice(0, 3);

  const handleContinue = () => {
    const nameCol = mappings.find(m => m.field === 'name');
    const firstCol = mappings.find(m => m.field === 'first_name');
    const lastCol = mappings.find(m => m.field === 'last_name');
    const phoneCol = mappings.find(m => m.field === 'phone');
    const emailCol = mappings.find(m => m.field === 'email');

    const out: NormalizedRow[] = [];
    dataRows.forEach((row, idx) => {
      let name = '';
      if (nameCol) {
        const raw = row[nameCol.columnIndex] ?? '';
        const commaSplit = splitCommaName(raw);
        name = commaSplit
          ? combineName(commaSplit.first, commaSplit.last)
          : normalizeName(raw);
      } else if (firstCol && lastCol) {
        name = combineName(
          row[firstCol.columnIndex] ?? '',
          row[lastCol.columnIndex] ?? '',
        );
      } else if (firstCol) {
        name = normalizeName(row[firstCol.columnIndex] ?? '');
      } else if (lastCol) {
        name = normalizeName(row[lastCol.columnIndex] ?? '');
      }

      const phone = phoneCol ? normalizePhone(row[phoneCol.columnIndex] ?? '') : '';
      const email = emailCol ? normalizeEmail(row[emailCol.columnIndex] ?? '') : '';

      if (!name && !phone && !email) return;
      out.push({
        rowKey: `row-${idx}`,
        name,
        phone,
        email,
      });
    });

    onContinue(out);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p
        style={{
          margin: 0,
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
        }}
      >
        We tried to match your columns automatically. Adjust anything that
        looks off, then continue.
      </p>

      {competitorProfile && !competitorMatched && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: 10,
            background: palette.g50,
            border: `1px solid ${palette.g150}`,
            borderRadius: radii.sm,
            fontSize: typography.size.caption,
            color: admin.caption,
            lineHeight: typography.lineHeight.relaxed,
          }}
        >
          <Info size={14} color={palette.g500} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            This doesn&rsquo;t look like a {competitorProfile.displayName}{' '}
            export — we&rsquo;re using auto-detection instead.
          </span>
        </div>
      )}

      {!hasName && (
        <div
          style={{
            padding: 12,
            background: palette.amberWash,
            border: `1px solid ${palette.amber}`,
            borderRadius: radii.sm,
            color: palette.amber,
            fontSize: typography.size.caption,
          }}
        >
          We couldn&rsquo;t find a Name column — which of these is the client
          name? Map it before continuing.
        </div>
      )}

      <div
        style={{
          border: `1px solid ${palette.g150}`,
          borderRadius: radii.md,
          background: palette.white,
          boxShadow: shadow.xs,
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: typography.size.caption }}>
          <thead>
            <tr style={{ background: palette.g50 }}>
              <Th>Your column</Th>
              <Th>Sample</Th>
              <Th>CrewBit field</Th>
            </tr>
          </thead>
          <tbody>
            {mappings.map(m => {
              const sample = dataRows.slice(0, 3).map(r => r[m.columnIndex] ?? '').filter(Boolean).join(', ');
              return (
                <tr key={m.columnIndex} style={{ borderTop: `1px solid ${palette.g150}` }}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {m.confidence === 'high' ? (
                        <Check size={14} color={palette.green} />
                      ) : m.confidence === 'low' ? (
                        <HelpCircle size={14} color={palette.amber} />
                      ) : null}
                      <span style={{ color: admin.header, fontWeight: typography.weight.medium }}>
                        {m.headerLabel || `Column ${m.columnIndex + 1}`}
                      </span>
                    </div>
                  </Td>
                  <Td>
                    <span style={{ color: admin.caption }}>
                      {sample || <span style={{ color: palette.g400 }}>—</span>}
                    </span>
                  </Td>
                  <Td>
                    <select
                      value={m.field}
                      onChange={e => updateField(m.columnIndex, e.target.value as CrewBitField)}
                      style={{
                        padding: '6px 8px',
                        borderRadius: radii.xs,
                        border: `1px solid ${palette.g200}`,
                        background: palette.white,
                        fontSize: typography.size.caption,
                        fontFamily: 'inherit',
                        color: admin.body,
                        cursor: 'pointer',
                      }}
                    >
                      {(Object.keys(FIELD_LABELS) as CrewBitField[]).map(f => (
                        <option key={f} value={f}>
                          {FIELD_LABELS[f]}
                        </option>
                      ))}
                    </select>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          fontSize: typography.size.caption,
          color: admin.caption,
          cursor: 'pointer',
          lineHeight: typography.lineHeight.relaxed,
        }}
      >
        <input
          type="checkbox"
          checked={noHeaderOverride}
          onChange={e => setNoHeaderOverride(e.target.checked)}
          style={{ marginTop: 2, cursor: 'pointer' }}
        />
        <span>
          This file has no header row.
          <span style={{ display: 'block', color: palette.g400 }}>
            Tick this if your spreadsheet&rsquo;s first row is client data,
            not column names.
          </span>
        </span>
      </label>

      {previewRows.length > 0 && (
        <div>
          <div
            style={{
              fontSize: typography.size.caption,
              fontWeight: typography.weight.semibold,
              color: admin.caption,
              marginBottom: 8,
            }}
          >
            First {previewRows.length} rows from your file
          </div>
          <div
            style={{
              border: `1px solid ${palette.g150}`,
              borderRadius: radii.md,
              background: palette.white,
              overflow: 'auto',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: typography.size.micro }}>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${palette.g150}` }}>
                    {Array.from({ length: columnCount }, (_, c) => (
                      <td
                        key={c}
                        style={{
                          padding: '8px 10px',
                          color: admin.body,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row[c] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!hasName}
          style={{
            padding: '10px 20px',
            background: hasName ? palette.accent : palette.g300,
            color: palette.white,
            border: 'none',
            borderRadius: radii.sm,
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            fontFamily: 'inherit',
            cursor: hasName ? 'pointer' : 'not-allowed',
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 12px',
        fontSize: typography.size.caption,
        fontWeight: typography.weight.semibold,
        color: admin.caption,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: '10px 12px', verticalAlign: 'top', color: admin.body }}>
      {children}
    </td>
  );
}
